/**
 * The chart's look, from the app's own tokens (the CSS variables), never from the spec: the server
 * sends Vega-Lite with no colours or fonts, and this file themes it for the page it sits on.
 */
import { useSyncExternalStore } from "react";

export interface ChartTokens {
  font: string;
  foreground: string;
  muted: string;
  border: string;
  destructive: string;
  /** The card's background: a downloaded PNG sits on it. */
  card: string;
  /** Series colours, in order. */
  palette: string[];
}

/** Labels the server draws as references, each in a fixed tone (not a series colour). */
const REFERENCE_TONES: Record<string, keyof Omit<ChartTokens, "font" | "palette" | "card">> = {
  "Average of full months": "muted",
  "Below zero": "destructive",
};

/** The chart series tokens (index.css), with their values for a page that lacks them. */
const PALETTE_TOKENS: [string, string][] = [
  ["--chart-1", "#2a78d6"],
  ["--chart-2", "#eb6834"],
  ["--chart-3", "#1aa876"],
  ["--chart-4", "#c78800"],
  ["--chart-5", "#e56c9a"],
  ["--chart-6", "#008300"],
  ["--chart-7", "#6250d6"],
  ["--chart-8", "#e34948"],
];

function hsl(styles: CSSStyleDeclaration, name: string, fallback: string): string {
  const value = styles.getPropertyValue(name).trim();
  return value ? `hsl(${value})` : fallback;
}

/** The tokens as the page has them now. */
export function readTokens(root: HTMLElement = document.documentElement): ChartTokens {
  const styles = getComputedStyle(root);
  const body = document.body ? getComputedStyle(document.body) : styles;
  return {
    font: body.fontFamily || "sans-serif",
    foreground: hsl(styles, "--foreground", "#0f172a"),
    muted: hsl(styles, "--muted-foreground", "#64748b"),
    border: hsl(styles, "--border", "#e2e8f0"),
    destructive: hsl(styles, "--destructive", "#ef4444"),
    card: hsl(styles, "--card", "#ffffff"),
    palette: PALETTE_TOKENS.map(([name, fallback]) => hsl(styles, name, fallback)),
  };
}

function sameTokens(a: ChartTokens, b: ChartTokens): boolean {
  return (
    a.font === b.font &&
    a.foreground === b.foreground &&
    a.muted === b.muted &&
    a.border === b.border &&
    a.destructive === b.destructive &&
    a.card === b.card &&
    a.palette.join() === b.palette.join()
  );
}

/* One observer for the page, shared by every chart. The root's style also changes for things that
 * are not the theme (the lip's drag writes its size there on every move), so the tokens are only
 * replaced, and the charts redrawn, when a chart token really changed. */
let shared: ChartTokens | null = null;
let observer: MutationObserver | null = null;
const listeners = new Set<() => void>();

/** Reads the tokens again; true when they changed. */
function refresh(): boolean {
  const next = readTokens();
  if (shared && sameTokens(shared, next)) return false;
  shared = next;
  return true;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (!observer) {
    observer = new MutationObserver(() => {
      if (refresh()) listeners.forEach((notify) => notify());
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "style", "data-theme"],
    });
    // Nobody watched while no chart was shown: catch up.
    refresh();
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size) return;
    observer?.disconnect();
    observer = null;
  };
}

function snapshot(): ChartTokens {
  shared ??= readTokens();
  return shared;
}

/** The tokens, read again whenever the root's class, style or theme attribute changes (a theme
 * switch), so a drawn chart follows the page live. The same object until a token changes. */
export function useChartTokens(): ChartTokens {
  return useSyncExternalStore(subscribe, snapshot);
}

/** The Vega config for these tokens: text, axes, grid and legend in the app's tones. */
export function chartConfig(tokens: ChartTokens): Record<string, unknown> {
  const text = { font: tokens.font, color: tokens.muted };
  return {
    background: null,
    font: tokens.font,
    view: { stroke: null },
    axis: {
      domainColor: tokens.border,
      gridColor: tokens.border,
      tickColor: tokens.border,
      labelColor: tokens.muted,
      labelFont: tokens.font,
      labelFontSize: 11,
      titleColor: tokens.muted,
      titleFont: tokens.font,
      titleFontSize: 11,
      titleFontWeight: 500,
    },
    axisX: { grid: false },
    legend: {
      orient: "bottom",
      direction: "horizontal",
      labelColor: tokens.foreground,
      labelFont: tokens.font,
      labelFontSize: 11,
      symbolSize: 80,
      title: null,
    },
    range: { category: tokens.palette },
    mark: { color: tokens.palette[0] },
    rule: { color: tokens.muted },
    point: { color: tokens.destructive },
    title: text,
  };
}

type Spec = Record<string, unknown>;
type Layer = { encoding?: Record<string, Record<string, unknown>>; data?: unknown };

const LOCAL_UNITS = new Set(["yearmonth", "yearmonthdate"]);

/** A channel (or a tooltip's list of them) reading its months and days in UTC, as they are stored:
 * parts saved before the server said so carry a local unit, a day early west of UTC. */
function utcTimeUnit<T>(channel: T): T {
  if (Array.isArray(channel)) return channel.map(utcTimeUnit) as T;
  const unit = (channel as { timeUnit?: unknown } | null)?.timeUnit;
  if (typeof unit !== "string" || !LOCAL_UNITS.has(unit)) return channel;
  return { ...channel, timeUnit: { unit, utc: true } };
}

/** The colour each label gets: references in their tone, series in palette order. */
export function colourScale(
  spec: Spec,
  tokens: ChartTokens
): { domain: string[]; range: string[] } {
  const domain: string[] = [];
  const values = ((spec.data as { values?: { series?: string }[] } | undefined)?.values ?? []).map(
    (row) => row.series
  );
  const layers = (spec.layer as Layer[] | undefined) ?? [];
  const datums = layers.map((layer) => layer.encoding?.color?.datum);
  const derived = layers.flatMap((layer) =>
    ((layer as { transform?: { calculate?: string; as?: string }[] }).transform ?? [])
      .filter((step) => step.as === "derived_series" && step.calculate)
      .map((step) => {
        try {
          return JSON.parse(step.calculate as string) as unknown;
        } catch {
          return null;
        }
      })
  );
  for (const label of [...values, ...derived, ...datums]) {
    if (typeof label === "string" && !domain.includes(label)) domain.push(label);
  }
  let next = 0;
  const range = domain.map((label) => {
    const tone = REFERENCE_TONES[label];
    return tone ? tokens[tone] : tokens.palette[next++ % tokens.palette.length];
  });
  return { domain, range };
}

/**
 * The server's spec, sized for its box and coloured: it fills the box's width and height, each
 * colour encoding shares one scale whose domain names every series and reference line, and months
 * and days read in UTC. A deep copy:
 * the part comes from the store, frozen, and Vega writes ids onto the data rows it reads.
 */
export function themedSpec(stored: Spec, tokens: ChartTokens): Spec {
  const spec = structuredClone(stored);
  const scale = colourScale(spec, tokens);
  const layers = ((spec.layer as Layer[] | undefined) ?? []).map((stored) => {
    const layer = stored.encoding
      ? {
          ...stored,
          encoding: Object.fromEntries(
            Object.entries(stored.encoding).map(([name, channel]) => [name, utcTimeUnit(channel)])
          ),
        }
      : stored;
    const color = layer.encoding?.color;
    if (!color || !scale.domain.length) return layer;
    return { ...layer, encoding: { ...layer.encoding, color: { ...color, scale } } };
  });
  return {
    ...spec,
    width: "container",
    height: "container",
    autosize: { type: "fit", contains: "padding" },
    layer: layers,
  };
}
